import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import type { TimerView } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { IconButton, Modal } from '../ui.js';

const PRESETS = [
  { label: '1 min', seconds: 60 },
  { label: '3 min', seconds: 180 },
  { label: '5 min', seconds: 300 },
  { label: '10 min', seconds: 600 },
  { label: '15 min', seconds: 900 },
];

function format(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * The server stores an absolute deadline, so each client simply counts down
 * locally. That keeps everyone in sync without a per-second broadcast, and a
 * client that sleeps and wakes recovers the correct time immediately.
 */
function useRemainingSeconds(timer: TimerView | null): number | null {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!timer?.isRunning) return;
    // Tick every second so the visible countdown actually advances. The memo
    // below reads `tick`, so each increment recomputes the remaining time.
    const interval = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(interval);
  }, [timer?.isRunning, timer?.endsAt]);

  return useMemo(() => {
    if (!timer) return null;
    if (!timer.isRunning) return timer.pausedRemainingSeconds ?? 0;
    // `tick` is a deliberate dependency: incrementing it every second is what
    // drives the recompute below. Without it the memo froze on its first value
    // and the countdown never moved.
    void tick;
    return Math.max(0, Math.round((new Date(timer.endsAt).getTime() - Date.now()) / 1000));
  }, [timer, tick]);
}

export function TimerWidget({ timer, canControl }: { timer: TimerView | null; canControl: boolean }) {
  const send = useRetroStore((state) => state.send);
  const [open, setOpen] = useState(false);
  const remaining = useRemainingSeconds(timer);

  const elapsed = timer?.isRunning === true && remaining === 0;
  const running = timer?.isRunning === true && remaining !== null && remaining > 0;
  const paused = Boolean(timer) && timer?.isRunning === false;

  const indicator = (
    <>
      <span aria-hidden>⏰</span>
      {timer ? (
        <span className="tabular-nums">
          {format(remaining ?? 0)}
          {paused ? ' (paused)' : ''}
        </span>
      ) : (
        // Idle, the word labels what the clock icon is for. A running timer
        // shows its countdown instead.
        <span>Timer</span>
      )}
    </>
  );

  if (!canControl) {
    // A passive countdown: non-interactive, with the restriction spelled out
    // instead of a button that looks clickable and silently does nothing.
    return (
      <div
        className={clsx(
          'card flex items-center gap-2 px-3 py-2 text-sm font-medium',
          elapsed && 'bg-rose-600 text-white',
          running && remaining !== null && remaining <= 30 && !elapsed && 'text-rose-600',
        )}
        title="Only the facilitator controls the timer"
        aria-label="Only the facilitator controls the timer"
      >
        {indicator}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          'card flex items-center gap-2 px-3 py-2 text-sm font-medium transition hover:shadow-panel',
          elapsed && 'bg-rose-600 text-white',
          running && remaining !== null && remaining <= 30 && !elapsed && 'text-rose-600',
        )}
        title="Set a timer for this step"
      >
        {indicator}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Timer" width="sm">
        {timer ? (
          <div className="mb-4 flex items-center justify-between rounded-xl bg-black/[0.03] px-4 py-3">
            <span className="text-2xl font-semibold tabular-nums">{format(remaining ?? 0)}</span>
            <div className="flex gap-1">
              {timer.isRunning ? (
                <IconButton label="Pause timer" onClick={() => void send('timer:pause')}>
                  ⏸
                </IconButton>
              ) : (
                <IconButton label="Resume timer" onClick={() => void send('timer:resume')}>
                  ▶️
                </IconButton>
              )}
              <IconButton
                label="Clear timer"
                onClick={() => {
                  void send('timer:stop');
                  setOpen(false);
                }}
              >
                ✕
              </IconButton>
            </div>
          </div>
        ) : null}

        <p className="mb-2 text-sm text-ink/60">Everyone sees the same countdown.</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.seconds}
              type="button"
              className="btn"
              onClick={() => {
                void send('timer:start', { durationSeconds: preset.seconds });
                setOpen(false);
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}
